import * as React from 'react';
import { connect } from 'react-redux';
import { Layout, Spin, Row, Col, Card, Table } from 'antd';
import { currencyFormatter, floatToPercentageString } from '../../utils/DataFormatterUtils';

import { nullSafeGet } from '../../utils/DataAccessUtils';
import { BarChart } from '../charts/BarChart';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { withRouter } from 'react-router';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import { clearCategoryFilters, updateCategoryFilters } from '../../actions/spend_analytics_actions';
import {
	fetchSpendDataByCategory,
	fetchSpendDataByMonth,
} from '../../thunks/spend_analytics_thunks';
import { debounce } from '../../utils/PerformanceUtils';
import { EmptyState } from '../empty_state/EmptyState';
import { StackedAreaChart } from '../charts/StackedAreaChart';
import moment from 'moment';
import { subcategoriesDisplayNamesMapper } from '../../utils/AnalyticsUtils';
import LogOnMountWithStandardEventProperties from '../log_on_mount_with_standard_event_properties/LogOnMountWithStandardEventProperties';

const style = require('./SpendAnalyticsOverviewCategoriesPage.less');
const DataSet = require('@antv/data-set');
const { Content } = Layout;
const isEqual = require('fast-deep-equal');
const spendByMonthConfig = {
	xAxisName: 'yearMonthKey',
	yAxisName: 'monthlyTotal',
	position: 'yearMonthKey*monthlyTotal',
	fillArea: false,
	transformer: (data) => {
		const ds = new DataSet();
		const dv = ds.createView().source(data);
		dv.transform({
			type: 'fold',
			fields: [
				subcategoriesDisplayNamesMapper['laborMonthlyTotal'],
				subcategoriesDisplayNamesMapper['materialMonthlyTotal'],
				subcategoriesDisplayNamesMapper['freightMonthlyTotal'],
				subcategoriesDisplayNamesMapper['travelMonthlyTotal'],
				subcategoriesDisplayNamesMapper['miscMonthlyTotal'],
				subcategoriesDisplayNamesMapper['taxMonthlyTotal'],
			], // expand fields into separate records
			key: 'subcategory', // new key field
			value: 'monthlyTotal', // new value field
		});
		return dv;
	},
	color: 'subcategory',
	cols: {
		yearMonthKey: { alias: 'Month' },
		monthlyTotal: { alias: 'Spend' },
	},
	yAxisLabel: {
		formatter: (val) => (val > 0 ? currencyFormatter(0, 'USD')(val) : '$0'),
		textStyle: {
			fontSize: '12',
			fontFamily: 'Roboto',
			fill: 'rgba(0,0,0,0.85)',
		},
	},
	xAxisLabel: {
		formatter: (val) => moment(val, 'YYYY-MM').format('MMM YYYY'),
		textStyle: {
			fontSize: '12',
			fontFamily: 'Roboto',
			fill: 'rgba(0,0,0,0.85)',
		},
	},
};
const spendByCategoryChartConfig = {
	xAxisName: 'fieldName',
	yAxisName: 'subTotal',
	position: 'fieldName*subTotal',
	fillArea: false,
	color: 'fieldName',
	cols: {
		fieldName: { alias: 'Spend Category' },
		subTotal: { alias: 'Spend' },
	},
	yAxisLabel: {
		formatter: (val) => (val > 0 ? currencyFormatter(0, 'USD')(val) : '$0'),
		textStyle: {
			fontSize: '12',
			fontFamily: 'Roboto',
			fill: 'rgba(0,0,0,0.85)',
		},
	},
	xAxisLabel: {
		textStyle: {
			fontSize: '12',
			fontFamily: 'Roboto',
			fill: 'rgba(0,0,0,0.85)',
		},
	},
};

class SpendAnalyticsOverviewCategoriesPage extends React.Component<any, any> {
	state = {
		initialLoading: true,
	};
	debouncedCallback = debounce(
		() => {
			const { fetchSpendData, fetchTimeSeriesSpendData, analytics } = this.props;
			const { initialLoading } = this.state;
			const promise1 = fetchSpendData(analytics.categoryFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.categoryFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
			}
		},
		500,
		false
	);

	componentDidMount() {
		const { fetchSpendData, fetchTimeSeriesSpendData, analytics } = this.props;
		const { initialLoading } = this.state;
		if (analytics.categoryFilters.startDate && analytics.categoryFilters.endDate) {
			const promise1 = fetchSpendData(analytics.categoryFilters);
			const promise2 = fetchTimeSeriesSpendData(analytics.categoryFilters);
			if (initialLoading) {
				Promise.all([promise1, promise2]).then(() => this.setState({ initialLoading: false }));
			}
		}
	}

	componentDidUpdate(prevProps) {
		const { analytics } = this.props;
		if (!isEqual(this.props.analytics.categoryFilters, prevProps.analytics.categoryFilters)) {
			this.debouncedCallback();
		}
	}

	render() {
		const { saveCategoryFilters, clearAllCategoryFilters, analytics, accountActivated } =
			this.props;
		const { initialLoading } = this.state;
		const { spendByCategory, spendByMonth, categoryFilters } = analytics;

		const spendByCategoryData =
			spendByCategory.data &&
			spendByCategory.data
				.map((el) => ({
					...el,
					subTotal: parseFloat(el.subTotal),
					percentageOfTotal: parseFloat(el.percentageOfTotal),
				}))
				.sort((a, b) => parseFloat(a.subTotal) - parseFloat(b.subTotal));

		const spendByMonthData =
			spendByMonth.data &&
			spendByMonth.data.map((el) => ({
				...el,
				monthlyTotal: parseFloat(el.monthlyTotal),
				[subcategoriesDisplayNamesMapper['freightMonthlyTotal']]: parseFloat(
					el.freightMonthlyTotal
				),
				[subcategoriesDisplayNamesMapper['laborMonthlyTotal']]: parseFloat(el.laborMonthlyTotal),
				[subcategoriesDisplayNamesMapper['materialMonthlyTotal']]: parseFloat(
					el.materialMonthlyTotal
				),
				[subcategoriesDisplayNamesMapper['miscMonthlyTotal']]: parseFloat(el.miscMonthlyTotal),
				[subcategoriesDisplayNamesMapper['taxMonthlyTotal']]: parseFloat(el.taxMonthlyTotal),
				[subcategoriesDisplayNamesMapper['travelMonthlyTotal']]: parseFloat(el.travelMonthlyTotal),
			}));

		const defaultSortOrder: 'ascend' | 'descend' = 'descend';
		const tooltipTemplate = `
                    <li data-index={index}>
                        <!-- The marker for each record -->
                        <span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
                        {name}: \${value}
                    </li>
        `;
		const spendOverTimeTooltipItemTemplate = `
                    <li data-index={index}>
                        <!-- The marker for each record -->
                        <span style="background-color:{color};width:8px;height:8px;border-radius:50%;display:inline-block;margin-right:8px;"></span>
                        {name}: \${value}
                    </li>
        `;

		const spendByCategoryColumns = [
			{
				title: 'Spend Category',
				dataIndex: 'fieldName',
				render: (text) => (
					<Ellipsis tooltip={true} style={{ display: 'inline' }} length={28}>
						{text}
					</Ellipsis>
				),
			},
			{
				title: 'Total Spend',
				dataIndex: 'subTotal',
				defaultSortOrder: defaultSortOrder,
				sorter: (a, b) => a.subTotal - b.subTotal,
				render: (amount) => currencyFormatter(0, 'USD')(amount),
			},
			{
				title: '% of Total',
				dataIndex: 'percentageOfTotal',
				render: (num) => floatToPercentageString(num / 100),
				width: 100,
			},
		];

		return (
			<Content
				className="analyticsOverviewCategoriesPage"
				style={{ padding: '0 0.5em', position: 'relative' }}
			>
				<LogOnMountWithStandardEventProperties eventType="visited spend analytics categories page" />
				{/*<BackTop/>*/}
				{accountActivated ? (
					<div>
						<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
									}}
								>
									<AnalyticsControlBar
										updateFilters={saveCategoryFilters}
										clearFilters={clearAllCategoryFilters}
										locationIds={categoryFilters.locationIds}
										startDate={categoryFilters.startDate}
										supplierFacilityIds={categoryFilters.supplierFacilityIds}
										endDate={categoryFilters.endDate}
										isCapex={categoryFilters.isCapex}
										isPM={categoryFilters.isPM}
										workCreatedAtStartDate={categoryFilters.workCreatedAtStartDate}
										workCreatedAtEndDate={categoryFilters.workCreatedAtEndDate}
										workCompletedAtStartDate={categoryFilters.workCompletedAtStartDate}
										workCompletedAtEndDate={categoryFilters.workCompletedAtEndDate}
										filterConfig={{
											workCreatedAt: true,
											workCompletedAt: true,
											capEx: true,
											isSpendAnalytics: true,
										}}
									/>
									<div></div>
								</div>
							</Col>
						</Row>

						{spendByMonth.loading ||
						initialLoading ||
						(spendByCategoryData && spendByCategoryData.length > 0) ? (
							[
								<>
									<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
										<Col span={24}>
											<Card>
												<Spin spinning={spendByCategory.loading || initialLoading}>
													{spendByCategoryData && spendByCategoryData.length > 0 ? (
														<Table
															pagination={{ pageSize: 8 }}
															columns={spendByCategoryColumns}
															rowKey={(el) => el['category']}
															dataSource={spendByCategoryData}
														/>
													) : null}
												</Spin>
											</Card>
										</Col>
									</Row>
									<Row>
										<Col span={24}>
											<Card>
												<Spin spinning={spendByCategory.loading || initialLoading}>
													{spendByCategoryData && spendByCategoryData.length > 0 ? (
														<BarChart
															height={spendByCategoryData.length * 32}
															chartPadding={[40, 40, 40, 200]}
															data={spendByCategoryData}
															showTooltipTitle={false}
															tooltipItemTemplate={tooltipTemplate}
															xAxisName={nullSafeGet('xAxisName', spendByCategoryChartConfig)}
															yAxisName={nullSafeGet('yAxisName', spendByCategoryChartConfig)}
															xAxisLabel={nullSafeGet('xAxisLabel', spendByCategoryChartConfig)}
															yAxisLabel={nullSafeGet('yAxisLabel', spendByCategoryChartConfig)}
															fillArea={nullSafeGet('fillArea', spendByCategoryChartConfig)}
															cols={nullSafeGet('cols', spendByCategoryChartConfig)}
															color={nullSafeGet('color', spendByCategoryChartConfig)}
															position={nullSafeGet('position', spendByCategoryChartConfig)}
														/>
													) : null}
												</Spin>
											</Card>
										</Col>
									</Row>
								</>,

								<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
									<Col span={24}>
										<Card>
											<Spin spinning={spendByMonth.loading || initialLoading}>
												{spendByMonth.data && spendByMonth.data.length > 0 ? (
													<StackedAreaChart
														height={260}
														chartPadding={[40, 40, 40, 80]}
														data={spendByMonthConfig.transformer(spendByMonthData)}
														tooltipItemTemplate={tooltipTemplate}
														showTooltipTitle={false}
														xAxisName={nullSafeGet('xAxisName', spendByMonthConfig)}
														yAxisName={nullSafeGet('yAxisName', spendByMonthConfig)}
														yAxisLabel={nullSafeGet('yAxisLabel', spendByMonthConfig)}
														xAxisLabel={nullSafeGet('xAxisLabel', spendByMonthConfig)}
														fillArea={nullSafeGet('fillArea', spendByMonthConfig)}
														cols={nullSafeGet('cols', spendByMonthConfig)}
														color={nullSafeGet('color', spendByMonthConfig)}
														position={nullSafeGet('position', spendByMonthConfig)}
													/>
												) : null}
											</Spin>
										</Card>
									</Col>
								</Row>,
							]
						) : (
							<EmptyState
								height={undefined}
								headline={'No invoices found'}
								body={
									<div style={{ maxWidth: 488, textAlign: 'center' }}>
										We didn't find any invoice data. Try changing your filters or expanding the
										selected time period.
									</div>
								}
							/>
						)}
					</div>
				) : (
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
							/>
						}
						headline={"You haven't activated your account yet."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 560, marginBottom: 16 }}>
									Please check your email. We sent you a note with an activation link.
								</div>
							</div>
						}
					/>
				)}
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,

	accountActivated: state.session.accountActivated,
	currentUser: state.session.currentUser,
	analytics: state.spend_analytics,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveCategoryFilters: (filters) => dispatch(updateCategoryFilters(filters)),
	clearAllCategoryFilters: (filters) => dispatch(clearCategoryFilters()),
	fetchSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchSpendDataByCategory(ownProps.userType)(params, filters, pagination, sorting)),
	fetchTimeSeriesSpendData: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchSpendDataByMonth(ownProps.userType)(params, filters, pagination, sorting)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SpendAnalyticsOverviewCategoriesPage)
);
export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
